import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "react-query";

const queryClient = new QueryClient();

import AgencyListPage from "./pages/AgencyListPage";
import Agency from "./pages/AgencyPage";
import LoginPage from "./pages/LoginPage";
import SSOCallback from "./pages/SSOCallback";
import WorkflowsPage from "./pages/WorkflowsPage";
import HomePage from "./pages/HomePage";
import ExportDataPage from "./pages/ExportDataPage";
import SalesMetricsPage from "./pages/SalesMetricsPage";
import IssueTrackingPage from "./pages/IssueTrackingPage";
import MetricsPage from "./pages/MetricsPage";
import CostsPage from "./pages/CostsPage";
import PrDashboardPage from "./pages/PrDashboardPage";
import LoginAsPage from "./pages/LoginAsPage";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import adminApi from "./api/admin/adminApi";

// title = the browser tab's name for the page, e.g. "Pull Requests - Internal"
const ProtectedRoute: React.FC<{ children: React.ReactElement; title?: string }> = ({
  children,
  title,
}) => {
  const { isAuthenticated, logout } = useAdminAuth();
  useEffect(() => {
    document.title = title ? `${title} - Internal` : "Internal";
    return () => {
      document.title = "Internal";
    };
  }, [title]);
  const [status, setStatus] = useState<"checking" | "valid" | "invalid">(
    isAuthenticated ? "checking" : "invalid"
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus("invalid");
      return;
    }
    let active = true;
    adminApi
      .adminValidateToken()
      .then((resp) => {
        if (!active) return;
        if (resp.status === 200) {
          setStatus("valid");
        } else {
          // Stay on "checking" until logout settles; clearing the session flips
          // isAuthenticated, which re-runs this effect and redirects to /login.
          logout();
        }
      })
      .catch(() => {
        if (!active) return;
        logout();
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, logout]);

  if (status === "invalid") {
    return <Navigate to="/login" replace />;
  }
  if (status === "checking") {
    return null;
  }
  return children;
};

const App = () => {
  return (
    <MantineProvider theme={{/* your theme settings */}}>
      <QueryClientProvider client={queryClient}>
        <AdminAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/sso-callback" element={<SSOCallback />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <HomePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agencies"
                element={
                  <ProtectedRoute title="Agencies">
                    <AgencyListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agency/:agencyId"
                element={
                  <ProtectedRoute title="Agency">
                    <Agency />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/workflows"
                element={
                  <ProtectedRoute title="Running Workflows">
                    <WorkflowsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/export-data"
                element={
                  <ProtectedRoute title="Export Data">
                    <ExportDataPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sales-metrics"
                element={
                  <ProtectedRoute title="Sales Metrics">
                    <SalesMetricsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/issue-tracking"
                element={
                  <ProtectedRoute title="Issue Tracking">
                    <IssueTrackingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/metrics"
                element={
                  <ProtectedRoute title="Usage Metrics">
                    <MetricsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/costs"
                element={
                  <ProtectedRoute title="Software Costs">
                    <CostsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login-as"
                element={
                  <ProtectedRoute title="Login As">
                    <LoginAsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pull-requests"
                element={
                  <ProtectedRoute title="Pull Requests">
                    <PrDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AdminAuthProvider>
      </QueryClientProvider>
    </MantineProvider>
  );
};

export default App;
